"use client"

import { useEffect } from "react"
import { Paper, Group, Text, Badge, ActionIcon, Stack } from "@forestsync/ui"
import { TbX } from "react-icons/tb"

export interface OrderTransmissionResult {
  price: number
  filledVolume: number
  totalVolume: number
  currentAsk: number | null
}

interface OrderTransmissionCardProps {
  result: OrderTransmissionResult
  onDismiss: () => void
}

const AUTO_DISMISS_MS = 8000

export function OrderTransmissionCard({ result, onDismiss }: OrderTransmissionCardProps) {
  useEffect(() => {
    const id = setTimeout(onDismiss, AUTO_DISMISS_MS)
    return () => clearTimeout(id)
  }, [onDismiss])

  const isFilled = result.filledVolume >= result.totalVolume
  const isPartial = result.filledVolume > 0 && !isFilled

  const statusLabel = isFilled ? "Filled" : isPartial ? `Partial ${result.filledVolume}/${result.totalVolume}` : "Pending"
  const statusColor = isFilled ? "green" : isPartial ? "orange" : "blue"

  return (
    <Paper
      withBorder
      p="xs"
      radius="sm"
      style={{
        borderColor: "var(--mantine-color-green-7)",
        flexShrink: 0,
        marginBottom: 4,
      }}
    >
      <Group justify="space-between" wrap="nowrap" mb={4}>
        <Text size="xs" fw={700} c="green.5">Order Transmitted</Text>
        <ActionIcon size="xs" variant="subtle" onClick={onDismiss}>
          <TbX size={12} />
        </ActionIcon>
      </Group>
      <Stack gap={2}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed">Price</Text>
          <Text size="xs" ff="monospace">€{result.price.toFixed(2)}</Text>
        </Group>
        {result.currentAsk != null && (
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" c="dimmed">Current Ask</Text>
            <Text size="xs" ff="monospace">€{result.currentAsk.toFixed(2)}</Text>
          </Group>
        )}
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed">Status</Text>
          <Badge size="xs" color={statusColor} variant="light">{statusLabel}</Badge>
        </Group>
      </Stack>
    </Paper>
  )
}
